import React, { useEffect, useState, useMemo } from 'react';
import { Users, Search, MessageSquare, SquarePen, LifeBuoy, ChevronRight, X } from 'lucide-react';
import { useChatContext } from 'stream-chat-react';
import { useStreamChat } from '../../../context/StreamChatContext';
import CustomChannelListItem from './CustomChannelListItem';
import SupportPrompt from '../parts/SupportPrompt';
import { classifySupportQuery } from '../utils/supportIntent';
import { channelDisplayName } from '../utils/chatFormat';
import { openSupport } from '../../Layout/shell/shellEvents';
import styles from '../Chat.module.css';

/**
 * `supportEnabled` — show the support prompt / footer row (off for Lodgeic staff).
 * `staffNames`     — Lodgeic staff display names, so searching for one of them
 *                    shows the support prompt instead.
 */
const CustomChannelList = ({
  onChannelSelect, isMobile, onCreateChannel, activeChannelId, supportEnabled = true, staffNames = [],
}) => {
  const { client } = useChatContext();
  const { refreshUserPresence } = useStreamChat();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [tick, setTick] = useState(0);

  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!client) return undefined;
    let cancelled = false;
    let attempt = 0;
    let retryTimer = null;
    let refetchTimer = null;
    let presenceKey = '';
    const RETRY_DELAYS = [1000, 3000, 8000];

    // A failed or skipped load never leaves "No conversations yet" on screen:
    // it retries, refetches when the connection comes back, and only then
    // says the list couldn't load.
    const fetchChannels = async () => {
      const me = client.user?.id;
      // Not connected yet (or reconnecting): look again shortly; the
      // connection events below also refetch the moment it's back.
      if (!me) {
        clearTimeout(retryTimer);
        retryTimer = setTimeout(fetchChannels, 1500);
        return;
      }
      try {
        const response = await client.queryChannels(
          { type: 'messaging', members: { $in: [me] } },
          { last_message_at: -1 },
          { limit: 50 },
        );
        if (cancelled) return;
        attempt = 0;
        setChannels(response);
        setLoadError(false);
        setLoading(false);

        // Presence only when the set of people changes, not on every message.
        const userIds = new Set();
        response.forEach((channel) => {
          Object.values(channel.state.members || {}).forEach((member) => {
            if (member.user?.id && member.user.id !== me) userIds.add(member.user.id);
          });
        });
        const key = Array.from(userIds).sort().join(',');
        if (key && key !== presenceKey && refreshUserPresence) {
          presenceKey = key;
          await refreshUserPresence(Array.from(userIds));
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching channels:', error);
        if (attempt < RETRY_DELAYS.length) {
          retryTimer = setTimeout(fetchChannels, RETRY_DELAYS[attempt]);
          attempt += 1;
        } else {
          setLoadError(true);
          setLoading(false);
        }
      }
    };

    fetchChannels();
    // Bursts of events (a busy conversation) collapse into one query, which
    // keeps the list clear of Stream's rate limit.
    const refetch = () => {
      clearTimeout(refetchTimer);
      refetchTimer = setTimeout(fetchChannels, 400);
    };
    const onConnectionChanged = (event) => { if (event?.online !== false) refetch(); };
    // Unread counts and previews live on the channel objects; re-render on reads.
    const repaint = () => setTick((t) => t + 1);

    const subs = [
      ['channel.updated', refetch],
      ['channel.deleted', refetch],
      ['message.new', refetch],
      ['notification.message_new', refetch],
      ['notification.added_to_channel', refetch],
      ['connection.recovered', refetch],
      ['connection.changed', onConnectionChanged],
      ['notification.mark_read', repaint],
      ['notification.channel_mutes_updated', repaint],
      ['message.updated', repaint],
      ['message.deleted', repaint],
    ];
    subs.forEach(([e, fn]) => client.on(e, fn));

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      clearTimeout(refetchTimer);
      subs.forEach(([e, fn]) => client.off(e, fn));
    };
  }, [client, refreshUserPresence, reloadKey]);

  const retryLoad = () => {
    setLoading(true);
    setLoadError(false);
    setReloadKey((k) => k + 1);
  };

  const meId = client?.user?.id;
  const unreadTotal = channels.reduce((sum, c) => sum + (c.countUnread?.() || 0), 0);
  const query = searchQuery.trim().toLowerCase();

  const filteredChannels = useMemo(() => {
    let filtered = [...channels];
    if (activeFilter === 'unread') filtered = filtered.filter((c) => (c.countUnread?.() || 0) > 0);
    else if (activeFilter === 'groups') filtered = filtered.filter((c) => Object.keys(c.state.members || {}).length > 2);

    if (query) {
      filtered = filtered.filter((channel) => {
        if (channelDisplayName(channel, meId).toLowerCase().includes(query)) return true;
        if (channel.data?.name?.toLowerCase().includes(query)) return true;
        return Object.values(channel.state.members || {}).some((m) => (
          `${m.user?.name || ''} ${m.user?.id || ''}`.toLowerCase().includes(query)
        ));
      });
    }
    return filtered;
    // `tick` moves on reads and edits, which change unread counts on the same
    // channel objects: a conversation you read leaves the Unread tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, activeFilter, query, meId, tick]);

  const supportMatch = supportEnabled && query
    ? classifySupportQuery(searchQuery, { staffNames })
    : null;

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'unread', label: 'Unread', count: unreadTotal },
    { id: 'groups', label: 'Groups' },
  ];

  return (
    <div className={`${styles.sidebar} ${isMobile ? styles.mobile : ''}`}>
      <div className={styles.listHead}>
        <div className={styles.listHeadRow}>
          <h1 className={styles.listTitle}>Messages</h1>
          <button type="button" onClick={onCreateChannel} className={styles.iconBtnInk} aria-label="New message" title="New message">
            <SquarePen size={15} />
          </button>
        </div>

        <label className={styles.searchWrap}>
          <span className={styles.srOnly}>Search conversations</span>
          <Search size={15} className={styles.searchIcon} aria-hidden="true" />
          <input
            type="search"
            placeholder="Search people and conversations"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') setSearchQuery(''); }}
            className={styles.searchInput}
          />
          {searchQuery && (
            <button type="button" className={styles.searchClear} onClick={() => setSearchQuery('')} aria-label="Clear search">
              <X size={14} />
            </button>
          )}
        </label>

        <div className={styles.tabs} role="tablist" aria-label="Filter conversations" style={{ alignSelf: 'flex-start' }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeFilter === tab.id}
              className={`${styles.tab} ${activeFilter === tab.id ? styles.active : ''}`}
              onClick={() => setActiveFilter(tab.id)}
            >
              {tab.label}
              {tab.count > 0 && <span className={styles.tabCount}>{tab.count > 99 ? '99+' : tab.count}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.channelList}>
        {supportMatch?.strength === 'strong' && (
          <div style={{ padding: '4px 4px 8px' }}>
            <SupportPrompt match={supportMatch} layout="stacked" />
          </div>
        )}

        {loading ? (
          <div className={styles.emptyState}>
            <div className={styles.spinner} />
            <p className={styles.emptyText} style={{ marginTop: 12 }}>Loading conversations…</p>
          </div>
        ) : (
          <>
            {query && <div className={styles.listSection}>Conversations</div>}
            {filteredChannels.map((channel) => (
              <CustomChannelListItem
                key={channel.cid || channel.id}
                channel={channel}
                isActive={activeChannelId === channel.id}
                onClick={() => onChannelSelect(channel)}
                channelName={channelDisplayName(channel, meId)}
              />
            ))}
            {filteredChannels.length === 0 && (
              loadError && channels.length === 0 ? (
                <div className={styles.emptyState} role="alert">
                  <div className={styles.emptyIcon}><MessageSquare size={20} /></div>
                  <h4 className={styles.emptyTitle}>Couldn’t load conversations</h4>
                  <p className={styles.emptyText}>Check your connection and try again.</p>
                  <button type="button" onClick={retryLoad} className={`${styles.btnInk} ${styles.btnSm}`} style={{ marginTop: 12 }}>
                    Try again
                  </button>
                </div>
              ) : query ? (
                <div className={styles.listNote}>No conversations match.</div>
              ) : activeFilter === 'unread' ? (
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}><MessageSquare size={20} /></div>
                  <h4 className={styles.emptyTitle}>All caught up</h4>
                  <p className={styles.emptyText}>No unread messages.</p>
                </div>
              ) : activeFilter === 'groups' ? (
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}><Users size={20} /></div>
                  <h4 className={styles.emptyTitle}>No group conversations</h4>
                  <p className={styles.emptyText}>Start one by adding two or more people to a new message.</p>
                </div>
              ) : (
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}><Users size={20} /></div>
                  <h4 className={styles.emptyTitle}>No conversations yet</h4>
                  <p className={styles.emptyText}>Message the people you work with at your properties.</p>
                  <button type="button" onClick={onCreateChannel} className={`${styles.btnInk} ${styles.btnSm}`} style={{ marginTop: 12 }}>
                    New message
                  </button>
                </div>
              )
            )}
            {supportMatch?.strength === 'soft' && (
              <div style={{ padding: '10px 4px 4px' }}>
                <SupportPrompt match={supportMatch} />
              </div>
            )}
          </>
        )}
      </div>

      {supportEnabled && (
        <div className={styles.listFoot}>
          <button type="button" className={styles.supportRow} onClick={() => openSupport()}>
            <span className={styles.supportIcon} style={{ width: 30, height: 30, borderRadius: 8 }} aria-hidden="true">
              <LifeBuoy size={15} />
            </span>
            <span className={styles.supportRowText}>
              <span className={styles.supportRowTitle}>Lodgeic support</span>
              <span className={styles.supportRowSub}>Questions about the app? Chat with us</span>
            </span>
            <ChevronRight size={15} style={{ color: 'var(--lodge-onyx-500)' }} aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomChannelList;
